import { col, type Shape } from './base.ts';

export const INJ_COMPLIANCE_GAS: Shape = [
  col('giBhpCompliance', 'unitless', 'Gas-injection bottom-hole-pressure compliance.', { suffix: 'BHPCompliance' }),
  col('giTargetRateCompliance', 'unitless', 'Gas-injection target-rate compliance.', {
    suffix: 'TargetRateCompliance',
  }),
  col('giTechnicalRateCompliance', 'unitless', 'Gas-injection technical-rate compliance.', {
    suffix: 'TechnicalRateCompliance',
  }),
  col('giWhpCompliance', 'unitless', 'Gas-injection well-head-pressure compliance.', { suffix: 'WHPCompliance' }),
];

/**
 * Water-injection compliance measures on `DataDrivenRates_String_Agent`, applied
 * with `prefix:'wi'`. Same compliance suffix family as {@link INJ_COMPLIANCE_GAS}
 * but the canonical keeps the `wi` water-injection phase marker so the two phases'
 * compliance metrics stay distinct when composed in the same view.
 */
export const INJ_COMPLIANCE_WATER: Shape = [
  col('wiBhpCompliance', 'unitless', 'Water-injection bottom-hole-pressure compliance.', { suffix: 'BHPCompliance' }),
  col('wiTargetRateCompliance', 'unitless', 'Water-injection target-rate compliance.', {
    suffix: 'TargetRateCompliance',
  }),
  col('wiTechnicalRateCompliance', 'unitless', 'Water-injection technical-rate compliance.', {
    suffix: 'TechnicalRateCompliance',
  }),
  col('wiWhpCompliance', 'unitless', 'Water-injection well-head-pressure compliance.', { suffix: 'WHPCompliance' }),
];

/**
 * Daily allocated production volumes + uptime on
 * `AllocatedRates_Daily_String_Agent`, applied with `prefix:'dayAllocProd'`. The
 * `Allocated{Oil,Gas,Water}` volume tail does NOT match `PHASE_RATES`
 * (`OilRate`), so it is authored here. The volume canonicals keep the `day`
 * cadence marker so they stay distinct from the monthly allocated volumes
 * ({@link ALLOC_PROD_MONTH}) composed alongside them in the same view.
 */
export const ALLOC_PROD_DAY: Shape = [
  col('dayAllocatedOil', 'liquidVolume', 'Daily allocated oil production volume.', { suffix: 'AllocatedOil' }),
  col('dayAllocatedGas', 'gasVolume', 'Daily allocated gas production volume.', { suffix: 'AllocatedGas' }),
  col('dayAllocatedWater', 'liquidVolume', 'Daily allocated water production volume.', { suffix: 'AllocatedWater' }),
  col('activeHours', 'unitless', 'Active production hours in the day.', { suffix: 'ActiveHours' }),
];

/**
 * Monthly allocated production volumes + uptime on
 * `AllocatedRates_Daily_String_Agent`, applied with `prefix:'monthAllocProd'`.
 * Same allocated-volume tail as the daily group but with a monthly active-DAYS
 * uptime member (vs daily active-HOURS). The volume canonicals keep the `month`
 * cadence marker so they stay distinct from the daily allocated volumes
 * ({@link ALLOC_PROD_DAY}) composed alongside them in the same view.
 */
export const ALLOC_PROD_MONTH: Shape = [
  col('monthAllocatedOil', 'liquidVolume', 'Monthly allocated oil production volume.', { suffix: 'AllocatedOil' }),
  col('monthAllocatedGas', 'gasVolume', 'Monthly allocated gas production volume.', { suffix: 'AllocatedGas' }),
  col('monthAllocatedWater', 'liquidVolume', 'Monthly allocated water production volume.', {
    suffix: 'AllocatedWater',
  }),
  col('activeDays', 'unitless', 'Active production days in the month.', { suffix: 'ActiveDays' }),
];

/**
 * Daily allocated GAS-injection volume + uptime on
 * `AllocatedInjection_String_Agent`, applied with `prefix:'dayGasInj'`. The
 * source DOUBLES the phase token — `dayGasInjGasInjVolume` (prefix `dayGasInj` +
 * suffix `GasInjVolume`) — preserved verbatim. Canonicals keep both the `day`
 * cadence and the gas phase marker so they stay distinct from the monthly gas
 * ({@link ALLOC_INJ_MONTH_GAS}) and daily water ({@link ALLOC_INJ_DAY_WATER})
 * groups composed alongside them in the same view.
 */
export const ALLOC_INJ_DAY_GAS: Shape = [
  col('dayGasInjectionVolume', 'gasVolume', 'Daily allocated gas injection volume.', { suffix: 'GasInjVolume' }),
  col('dayGasActiveHours', 'unitless', 'Active gas-injection hours in the day.', { suffix: 'ActiveHours' }),
];

/**
 * Daily allocated WATER-injection volume + uptime on
 * `AllocatedInjection_String_Agent`, applied with `prefix:'dayWaterInj'`. Doubled
 * phase token preserved (`dayWaterInjWaterInjVolume`). Canonicals keep both the
 * `day` cadence and the water phase marker so they stay distinct from the monthly
 * water ({@link ALLOC_INJ_MONTH_WATER}) and daily gas ({@link ALLOC_INJ_DAY_GAS})
 * groups composed alongside them in the same view.
 */
export const ALLOC_INJ_DAY_WATER: Shape = [
  col('dayWaterInjectionVolume', 'liquidVolume', 'Daily allocated water injection volume.', {
    suffix: 'WaterInjVolume',
  }),
  col('dayWaterActiveHours', 'unitless', 'Active water-injection hours in the day.', { suffix: 'ActiveHours' }),
];

/**
 * Monthly allocated GAS-injection volume + uptime on
 * `AllocatedInjection_String_Agent`, applied with `prefix:'monthGasInj'`. Doubled
 * phase token preserved (`monthGasInjGasInjVolume`); monthly active-DAYS uptime.
 * Canonicals keep both the `month` cadence and the gas phase marker so they stay
 * distinct from the daily gas ({@link ALLOC_INJ_DAY_GAS}) and monthly water
 * ({@link ALLOC_INJ_MONTH_WATER}) groups composed alongside them in the same view.
 */
export const ALLOC_INJ_MONTH_GAS: Shape = [
  col('monthGasInjectionVolume', 'gasVolume', 'Monthly allocated gas injection volume.', { suffix: 'GasInjVolume' }),
  col('monthGasActiveDays', 'unitless', 'Active gas-injection days in the month.', { suffix: 'ActiveDays' }),
];

/**
 * Monthly allocated WATER-injection volume + uptime on
 * `AllocatedInjection_String_Agent`, applied with `prefix:'monthWaterInj'`.
 * Doubled phase token preserved (`monthWaterInjWaterInjVolume`); monthly
 * active-DAYS uptime. Canonicals keep both the `month` cadence and the water phase
 * marker so they stay distinct from the daily water ({@link ALLOC_INJ_DAY_WATER})
 * and monthly gas ({@link ALLOC_INJ_MONTH_GAS}) groups composed alongside them in
 * the same view.
 */
export const ALLOC_INJ_MONTH_WATER: Shape = [
  col('monthWaterInjectionVolume', 'liquidVolume', 'Monthly allocated water injection volume.', {
    suffix: 'WaterInjVolume',
  }),
  col('monthWaterActiveDays', 'unitless', 'Active water-injection days in the month.', { suffix: 'ActiveDays' }),
];

/**
 * Measured GAS-injection rate/pressure/choke triplet on
 * `MeasuredInjection_String_Agent`, applied with `prefix:'gasInj'`. The injection
 * suffix family (`Rate`, `WellheadPress`, `ChokeOpening`) is shared across both
 * injection phases at the source, so the canonical keeps the `gasInj` phase marker
 * to stay distinct from the water-injection triplet ({@link MEASURED_INJ_WATER})
 * composed alongside it in the same view.
 */
export const MEASURED_INJ_GAS: Shape = [
  col('gasInjectionRate', 'gasRateModel', 'Measured gas-injection rate from the wellhead meter.', { suffix: 'Rate' }),
  col('gasInjectionWellheadPressure', 'pressureAbsolute', 'Measured gas-injection wellhead pressure.', {
    suffix: 'WellheadPress',
  }),
  col('gasInjectionChokeOpening', 'fraction', 'Measured gas-injection choke opening.', { suffix: 'ChokeOpening' }),
];

/**
 * Measured WATER-injection rate/pressure/choke triplet on
 * `MeasuredInjection_String_Agent`, applied with `prefix:'waterInj'`. Same
 * injection suffix family as {@link MEASURED_INJ_GAS} but the canonical keeps the
 * `waterInj` phase marker so the two phases stay distinct in the same view.
 */
export const MEASURED_INJ_WATER: Shape = [
  col('waterInjectionRate', 'liquidRate', 'Measured water-injection rate from the wellhead meter.', { suffix: 'Rate' }),
  col('waterInjectionWellheadPressure', 'pressureAbsolute', 'Measured water-injection wellhead pressure.', {
    suffix: 'WellheadPress',
  }),
  col('waterInjectionChokeOpening', 'fraction', 'Measured water-injection choke opening.', { suffix: 'ChokeOpening' }),
];

/**
 * String-level fluid composition on `StringFluidComposition_Agent`. Bare source
 * columns — `suffix === canonical`, `prefix:''` (single-concern passthrough).
 * VERBATIM source casing preserved for the mol components: `co2`/`h2s`/`n2`
 * (lowercase-leading) and `iC4`/`iC5`/`nC4`/`nC5` (lowercase prefix, uppercase
 * C). Distinct from the PTS `PTS_FLUID_COMPOSITION` (different member set:
 * alkane breakdown `c1..nC5` + condensate/fluid-type/specific-gas-gravity).
 */
